import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Search, Loader2, Users as UsersIcon, X, Save, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { userAPI } from '../utils/api';
import { useEntityStatus } from '../hooks/useEntityStatus';
import { EntityStatusFilter } from '../components/EntityStatusFilter';
import EntityStatusModal from '../components/EntityStatusModal';
import UserCard from '../components/UserCard';
import Pagination from '../components/Pagination';

const validateUserForm = (form, isEdit = false) => {
  if (!form.firstName.trim()) return 'Ad daxil edin';
  if (!form.lastName.trim()) return 'Soyad daxil edin';
  if (!form.username.trim()) return 'Username daxil edin';
  if (!/^\S+@\S+\.\S+$/.test(form.username)) return 'Email düzgün deyil';
  if (!isEdit && !form.password) return 'Şifrə daxil edin';
  if (form.password && form.password.length < 6) return 'Şifrə minimum 6 simvol olmalıdır';
  return null;
};

const Users = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 12;
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ firstName: '', lastName: '', username: '', password: '' });
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const searchTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  const fetchUsers = useCallback(
    async (searchTerm = '', currentPage = page, currentStatusFilter = 'active') => {
      try {
        setLoading(true);
        const params = { status: currentStatusFilter, page: currentPage, limit };
        if (searchTerm) params.search = searchTerm;
        const response = await userAPI.getAll(params);
        setUsers(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setTotal(response.data.total || 0);
      } catch (error) {
        toast.error(t('users.loadError'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page]
  );

  const {
    statusFilter, setStatusFilter, filteredEntities,
    requestToggle, confirmToggle, closeModal, modalEntity, togglingId,
  } = useEntityStatus({
    entities: users,
    refreshFn: () => fetchUsers(searchQuery, page, statusFilter),
    toggleFn: userAPI.toggleStatus,
    entityName: t('users.user'),
  });

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchUsers(searchQuery, 1);
    }, 300);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    setPage(1);
    fetchUsers(searchQuery, 1, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    fetchUsers(searchQuery, page, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const error = validateUserForm(addForm, false);
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    try {
      await userAPI.create({
        firstName: addForm.firstName.trim(),
        lastName: addForm.lastName.trim(),
        username: addForm.username.trim().toLowerCase(),
        password: addForm.password,
      });
      toast.success(t('users.createSuccess'));
      setAddForm({ firstName: '', lastName: '', username: '', password: '' });
      setShowAddForm(false);
      setEditingUser(null);
      setPage(1);
      fetchUsers(searchQuery, 1, statusFilter);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Xəta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditStart = (user) => {
    setShowAddForm(false);
    setEditingUser(user);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      password: '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditCancel = () => {
    setEditingUser(null);
    setEditForm({});
  };

  const handleEditSave = async () => {
    if (!editingUser) return;
    const error = validateUserForm(editForm, true);
    if (error) {
      toast.error(error);
      return;
    }
    const updateData = {
      firstName: editForm.firstName.trim(),
      lastName: editForm.lastName.trim(),
      username: editForm.username.trim().toLowerCase(),
    };
    if (editForm.password && editForm.password.length >= 6) {
      updateData.password = editForm.password;
    }
    try {
      await userAPI.update(editingUser._id, updateData);
      toast.success(t('users.updateSuccess'));
      setEditingUser(null);
      fetchUsers(searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Xəta');
    }
  };

  const renderForm = (form, setter, isEdit = false) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('users.firstName')} *</label>
        <input
          type="text"
          value={form.firstName}
          onChange={(e) => setter({ ...form, firstName: e.target.value })}
          className="form-input"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('users.lastName')} *</label>
        <input
          type="text"
          value={form.lastName}
          onChange={(e) => setter({ ...form, lastName: e.target.value })}
          className="form-input"
        />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs font-semibold text-gray-500 mb-1.5">{t('users.username')} *</label>
        <input
          type="email"
          value={form.username}
          onChange={(e) => setter({ ...form, username: e.target.value })}
          placeholder={t('users.usernamePlaceholder')}
          className="form-input"
        />
      </div>
      <div className="sm:col-span-2">
        <label className="block text-xs font-semibold text-gray-500 mb-1.5">
          {isEdit ? t('users.password') : t('users.passwordRequired')}
        </label>
        <input
          type="password"
          value={form.password}
          onChange={(e) => setter({ ...form, password: e.target.value })}
          placeholder="••••••••"
          className="form-input"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('users.title')}</h1>
          <p className="text-gray-500 text-sm mt-1">{t('users.subtitle')}</p>
        </div>
        {!editingUser && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center"
          >
            {showAddForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showAddForm ? t('users.close') : t('users.createNew')}
          </button>
        )}
      </div>

      {showAddForm && !editingUser && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 accordion-enter">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary-600" /> {t('users.createNew')}
          </h3>
          <form onSubmit={handleAdd}>
            {renderForm(addForm, (v) => setAddForm(v), false)}
            <div className="flex justify-end mt-4">
              <button type="submit" disabled={submitting} className="btn-primary">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                {t('users.create')}
              </button>
            </div>
          </form>
        </div>
      )}

      {editingUser && (
        <div className="bg-white rounded-xl shadow-sm border border-blue-200 p-6 accordion-enter">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              {t('users.firstName')} {t('users.save')} — {editingUser.firstName} {editingUser.lastName}
            </h3>
            <button onClick={handleEditCancel} className="text-gray-400 hover:text-red-500 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          {renderForm(editForm, (v) => setEditForm(v), true)}
          <div className="flex justify-end gap-3 mt-4">
            <button type="button" onClick={handleEditCancel} className="btn-secondary">
              <X className="w-4 h-4" /> {t('common.cancel')}
            </button>
            <button type="button" onClick={handleEditSave} className="btn-primary">
              <Save className="w-4 h-4" /> {t('users.save')}
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('users.searchPlaceholder')}
              className="form-input pl-10 w-full"
            />
          </div>
          <EntityStatusFilter value={statusFilter} onChange={setStatusFilter} />
        </div>
        <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
          <UsersIcon className="w-4 h-4" />
          <span>
            {t('users.totalCount')}: <strong className="text-gray-900">{total}</strong> {t('users.user')}
          </span>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
          <p className="text-gray-500 mt-2">{t('common.loading')}</p>
        </div>
      ) : filteredEntities.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-16 text-center">
          <UsersIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium text-gray-600">{t('users.empty')}</p>
          <p className="text-sm mt-1 text-gray-400">{t('users.emptyHint')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEntities.map((u) => (
              <UserCard
                key={u._id}
                user={u}
                onEdit={handleEditStart}
                onToggleStatus={requestToggle}
                toggling={togglingId === u._id}
              />
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}

      <EntityStatusModal
        isOpen={modalEntity !== null}
        entity={modalEntity}
        entityName={t('users.user')}
        displayName={modalEntity ? `${modalEntity.firstName} ${modalEntity.lastName}` : ''}
        onConfirm={confirmToggle}
        onClose={closeModal}
        submitting={togglingId !== null}
      />
    </div>
  );
};

export default Users;
